import { get, set, del } from 'idb-keyval'
import type {
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client'

/**
 * IndexedDB-backed persister for TanStack Query's PersistQueryClientProvider.
 *
 * Replaces the localStorage `createSyncStoragePersister` used for the desktop
 * offline menu cache. Product `image_url`s are multi-MB base64 data URLs, so a
 * full menu snapshot is tens of MB — far past localStorage's ~5 MB quota, which
 * made the sync persister silently fail (the "offline menu" never actually
 * worked). IndexedDB has a much larger budget and stores the client object via
 * structured clone, so there's no giant `JSON.stringify` of the base64 blobs.
 *
 * Returns a no-op persister when there's no `window`/IndexedDB (SSR / unsupported)
 * so the app degrades to plain in-memory caching instead of throwing.
 */
export function createIdbPersister(
  idbKey: IDBValidKey = 'bhookly-rq-cache',
): Persister {
  if (typeof window === 'undefined' || !('indexedDB' in window)) {
    return {
      persistClient: async () => {},
      restoreClient: async () => undefined,
      removeClient: async () => {},
    }
  }

  return {
    persistClient: async (client: PersistedClient) => {
      await set(idbKey, client)
    },
    restoreClient: async () => {
      return await get<PersistedClient>(idbKey)
    },
    removeClient: async () => {
      await del(idbKey)
    },
  }
}
